import React, { useState, useEffect, useCallback } from 'react';
import ProductAutocomplete from './ProductAutocomplete';
import OfficialBadge from './OfficialBadge';
import ScoreSlider from './ScoreSlider';
import AttachmentPicker from './AttachmentPicker';
import { useI18n } from './i18n';
import {
  ABTM_PROTOCOLS, ABTM_ELEMENTS, ABTM_FIELDS, ABTM_GROUPS, ABTM_MAX_TOTAL, abtmResult,
} from './abtmSheet';

const DRAFT_KEY = 'tnh2_draft_vinegar';

export const VINEGAR_KINDS = ['traditional_modena_dop', 'traditional_reggio_dop', 'balsamic_modena_igp', 'other'];
// Perceived-intensity marks (Giudici & Lemmetti research questionnaire); no total is computed
const GROUPS = [
  { key: 'visual', ratings: ['visual_viscosity', 'visual_colour', 'visual_clearness', 'visual_gloss'] },
  { key: 'olfactory', ratings: ['olfactory_pungency', 'olfactory_persistence', 'olfactory_overall'] },
  { key: 'gustatory', ratings: ['gustatory_consistency', 'gustatory_sweetness', 'gustatory_acidity_intensity',
    'gustatory_acidity_persistence', 'gustatory_astringency', 'gustatory_saltiness', 'gustatory_bitterness'] },
  { key: 'final', ratings: ['overall'] },
];

const INITIAL = {
  product_name: '', producer: '', origin: '', variety: '', harvest_year: '',
  protocol: 'abtm_dop', event: '', table_label: '',
  sample_code: '', vinegar_kind: '', declared_age_years: '',
  abtm_density: null, abtm_colour: null, abtm_clarity: null, abtm_frankness: null, abtm_finesse: null, abtm_olfactory_intensity: null, abtm_olfactory_acidity: null, abtm_fullness: null, abtm_gustatory_intensity: null, abtm_flavour: null, abtm_gustatory_acidity: null, abtm_harmony: null, abtm_final: null,
  visual_viscosity: null, visual_colour: null, visual_clearness: null, visual_gloss: null, olfactory_pungency: null, olfactory_persistence: null, olfactory_overall: null, gustatory_consistency: null, gustatory_sweetness: null, gustatory_acidity_intensity: null, gustatory_acidity_persistence: null, gustatory_astringency: null, gustatory_saltiness: null, gustatory_bitterness: null, overall: null,
  tasting_date: new Date().toISOString().slice(0, 10), venue: '', taster: '',
  notes: '', visibility: 'private',
};

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...INITIAL, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return INITIAL;
}

// Form state from an API sheet (null values keep the empty default; numbers become strings for the inputs)
const NUMBER_INPUTS = ['harvest_year', 'declared_age_years'];
function fromSheet(sheet) {
  const out = { ...INITIAL };
  Object.keys(INITIAL).forEach((k) => {
    if (sheet[k] === undefined || sheet[k] === null) return;
    out[k] = NUMBER_INPUTS.includes(k) ? String(sheet[k]) : sheet[k];
  });
  out.protocol = sheet.protocol || 'research_profile';   // sheets saved before v5 are research-profile sheets
  return out;
}

const emptyToNull = (value) => (value === '' || value === undefined ? null : value);
const toNumber = (value, parse) => (value === '' || value === null || value === undefined ? null : parse(value));

function VinegarForm({ onSubmit, initialData, onCancel }) {
  const { t, tc } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [attachmentItems, setAttachmentItems] = useState([]);

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const handleChange = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);
  const handleInput = (e) => handleChange(e.target.name, e.target.value);
  const abtm = form.protocol === 'abtm_dop';
  const result = abtmResult(form);

  const handleProtocol = (protocol) => {
    setForm((prev) => ({
      ...prev, protocol,
      vinegar_kind: protocol === 'abtm_dop' && !prev.vinegar_kind ? 'traditional_modena_dop' : prev.vinegar_kind,
    }));
  };

  const handleReset = () => {
    if (!window.confirm(t('vinegar.confirm_reset'))) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  // Sheet data in the API format (computed fields such as the total are never sent)
  const buildData = () => {
    if (!form.product_name.trim()) { alert(t('vinegar.alert_product_name')); return null; }
    const data = {
      ...form,
      harvest_year: toNumber(form.harvest_year, (v) => parseInt(v, 10)),
      declared_age_years: toNumber(form.declared_age_years, (v) => parseInt(v, 10)),
    };
    ABTM_FIELDS.forEach((k) => { data[k] = toNumber(form[k], (v) => parseInt(v, 10)); });
    Object.keys(data).forEach((k) => { if (typeof data[k] === 'string') data[k] = emptyToNull(data[k]); });
    return data;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = buildData();
    if (!data) return;
    onSubmit(data, attachmentItems);
    if (editing) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      {abtm
        ? <OfficialBadge status="formal" source={t('vinegar.abtm.badge_source')} note={t('vinegar.abtm.badge_note')} sourceNote={t('products.source_note.vinegar_abtm')} />
        : <OfficialBadge status="unofficial" source={t('vinegar.badge_source')} note={t('vinegar.badge_note')} sourceNote={t('products.source_note.vinegar')} />}

      <div className="form-field">
        <label>{t('vinegar.f.protocol')}</label>
        <div className="chip-group" role="radiogroup" aria-label={t('vinegar.f.protocol')}>
          {ABTM_PROTOCOLS.map((protocol) => (
            <button type="button" key={protocol} role="radio" aria-checked={form.protocol === protocol}
              className={`chip ${form.protocol === protocol ? 'selected' : ''}`}
              onClick={() => handleProtocol(protocol)}>{tc('vinegar.protocol', protocol)}</button>
          ))}
        </div>
        <small className="import-hint">{t('vinegar.protocol_hint')}</small>
      </div>

      {/* ── Vinegar ── */}
      <fieldset>
        <legend>{t('vinegar.sec.info')}</legend>
        <div className="form-grid">
          <div className="form-field">
            <label>{t('vinegar.f.product_name')}</label>
            <ProductAutocomplete
              category="vinegar"
              value={form.product_name}
              required
              onChange={(fields) => setForm((prev) => ({ ...prev, ...fields }))}
            />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.producer')}</label>
            <input name="producer" value={form.producer} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.origin')}</label>
            <input name="origin" value={form.origin} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.variety')}</label>
            <input name="variety" value={form.variety} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.harvest_year')}</label>
            <input name="harvest_year" type="number" min={1900} max={2200} value={form.harvest_year} onChange={handleInput} />
          </div>
          {abtm && (
            <>
              <div className="form-field">
                <label>{t('vinegar.f.event')}</label>
                <input name="event" value={form.event} onChange={handleInput} maxLength={200} />
              </div>
              <div className="form-field">
                <label>{t('vinegar.f.table_label')}</label>
                <input name="table_label" value={form.table_label} onChange={handleInput} maxLength={100} />
              </div>
            </>
          )}
          <div className="form-field">
            <label>{t(abtm ? 'vinegar.f.sample_number' : 'vinegar.f.sample_code')}</label>
            <input name="sample_code" value={form.sample_code} onChange={handleInput} maxLength={100} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.vinegar_kind')}</label>
            <select name="vinegar_kind" value={form.vinegar_kind} onChange={handleInput}>
              <option value="">—</option>
              {VINEGAR_KINDS.map((kind) => <option key={kind} value={kind}>{tc('vinegar.kind', kind)}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.declared_age_years')}</label>
            <input name="declared_age_years" type="number" min={0} max={200} step={1} value={form.declared_age_years} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.venue')}</label>
            <input name="venue" value={form.venue} onChange={handleInput} maxLength={200} />
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInput} maxLength={200} />
          </div>
        </div>
      </fieldset>

      {/* ── Evaluation ── */}
      {abtm ? (
        <fieldset>
          <legend>{t('vinegar.sec.evaluation')}</legend>
          <small className="import-hint">{t('vinegar.abtm.hint')}</small>
          {ABTM_GROUPS.map((group) => (
            <div key={group} className="vinegar-group">
              <h4>{t(`vinegar.abtm.group.${group}`)}</h4>
              {ABTM_ELEMENTS.filter((el) => el.group === group).map((el) => (
                <div key={el.field} className="form-field abtm-element">
                  <label>
                    {t(`vinegar.abtm.el.${el.field}`)}
                    {' '}<small className="import-hint">
                      {result.points[el.field] === null ? `—/${el.points[0]}` : `${result.points[el.field]}/${el.points[0]}`}
                    </small>
                  </label>
                  <div className="chip-group" role="radiogroup" aria-label={t(`vinegar.abtm.el.${el.field}`)}>
                    {el.points.map((points, i) => {
                      const level = i + 1;
                      const selected = Number(form[el.field]) === level;
                      return (
                        <button type="button" key={level} role="radio" aria-checked={selected}
                          className={`chip ${selected ? 'selected' : ''}`}
                          onClick={() => handleChange(el.field, selected ? null : level)}>
                          {el.descriptors ? `${el.descriptors[i]} · ${points}` : points}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ))}
          <div className="live-result">
            <span className="live-total">
              {result.total === null
                ? t('vinegar.abtm.total_pending', { done: result.chosen, count: result.count })
                : `${result.total}/${ABTM_MAX_TOTAL}`}
            </span>
            <small className="import-hint">{t('vinegar.abtm.total_hint')}</small>
          </div>
          <div className="form-field">
            <label>{t('vinegar.f.notes')}</label>
            <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('vinegar.ph.notes')} />
          </div>
        </fieldset>
      ) : (
        <fieldset>
          <legend>{t('vinegar.sec.evaluation')}</legend>
          <small className="import-hint">{t('vinegar.evaluation_hint')}</small>
          {GROUPS.map((group) => (
            <div key={group.key} className="vinegar-group">
              <h4>{t(`vinegar.group.${group.key}`)}</h4>
              {group.ratings.map((key) => (
                <ScoreSlider key={key} label={t(`vinegar.r.${key}`)} hint={t(`vinegar.hint.${key}`)}
                  value={form[key]} onChange={(value) => handleChange(key, value)} min={0} max={10} step={0.5} unit="/10" />
              ))}
            </div>
          ))}
          <div className="form-field">
            <label>{t('vinegar.f.notes')}</label>
            <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('vinegar.ph.notes')} />
          </div>
        </fieldset>
      )}

      {/* ── Attachments (only when creating: an existing sheet manages them in its detail view) ── */}
      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => handleChange('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('vinegar.save_edit') : t('vinegar.save_new')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default VinegarForm;
